import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Menu,
  List,
  ListItem,
  TaskAddIcon,
  ClockAddIcon,
  GlobalAddButton,
  CalendarAddXIcon,
} from "@activecollab/components";
import PropTypes from "prop-types";
import { useCreateTask } from "~/components/Providers/CreateTask/";
import UserAvailabilityRecordModalContentContainer from "~/components/UserProfile/UserAvailabilityRecordModalContentContainer.jsx";
import { getLoggedUser, isLoggedUserClientSelector } from "~/store/selectors/selector.users.jsx";
import { AddTimeRecord } from "../AddTimeRecord/AddTimeRecord.jsx";
import { useCreateTaskContext } from "../Providers/CreateTask/context/index.js";

export const MY_ADD_MENU = "my_add_menu";

export const MyAddMenu = ({ isLoggedUserClientPlus, isAvailabilityRecordsEnabled }) => {
  const { t } = useTranslation();
  const { openTaskForm } = useCreateTask();
  const { setModalOpenFrom } = useCreateTaskContext();
  const [showMenu, setShowMenu] = useState(false);
  const [showTimeRecordModal, setShowTimeRecordModal] = useState(false);
  const [showAvailabilityRecordModal, setShowAvailabilityRecordModal] = useState(false);
  const loggedUserRaw = useSelector(getLoggedUser);
  const loggedUser = useMemo(() => {
    return {
      id: loggedUserRaw.get("id"),
      name: loggedUserRaw.get("display_name"),
    };
  }, [loggedUserRaw]);
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);

  const handleOpenProjectList = useCallback(() => {
    setModalOpenFrom(MY_ADD_MENU);
    openTaskForm();
    setShowMenu(false);
  }, [openTaskForm, setModalOpenFrom]);

  const handleShowTimeRecordModal = useCallback(() => {
    setShowTimeRecordModal(true);
    setShowMenu(false);
  }, []);

  const handleCloseTimeRecordModal = useCallback(() => {
    setShowTimeRecordModal(false);
  }, []);

  const handleOpenMenu = useCallback(() => {
    setShowMenu(true);
  }, []);

  const handleShowAddAvailabilityRecordModal = useCallback(() => {
    setShowAvailabilityRecordModal(true);
    setShowMenu(false);
  }, []);

  const handleHideAddAvailabilityRecordModal = useCallback(() => {
    setShowAvailabilityRecordModal(false);
  }, []);

  return (
    <>
      <Menu
        open={showMenu}
        onOpen={handleOpenMenu}
        target={<GlobalAddButton className="tw-ml-4" />}
      >
        <List className="tw-py-2">
          <ListItem onClick={handleOpenProjectList}>
            <TaskAddIcon className="tw-mr-2 tw-w-5 tw-h-5" />
            {t("Add Task")}
          </ListItem>
          {!isLoggedUserClientPlus ? (
            <>
              {!isLoggedUserClient ? (
                <ListItem onClick={handleShowTimeRecordModal}>
                  <ClockAddIcon className="tw-mr-2 tw-w-5 tw-h-5" />
                  {t("Add Time Record")}
                </ListItem>
              ) : null}

              {isAvailabilityRecordsEnabled ? (
                <ListItem onClick={handleShowAddAvailabilityRecordModal}>
                  <CalendarAddXIcon className="tw-mr-2 tw-w-5 tw-h-5" />
                  {t("Add Availability Record")}
                </ListItem>
              ) : null}
            </>
          ) : null}
        </List>
      </Menu>
      <AddTimeRecord
        onClose={handleCloseTimeRecordModal}
        open={showTimeRecordModal}
        source="user_timesheet"
        hidePeoplePicker
      />
      <UserAvailabilityRecordModalContentContainer
        userId={loggedUser.id}
        userName={loggedUser.name}
        forOwners
        onClose={handleHideAddAvailabilityRecordModal}
        open={showAvailabilityRecordModal}
      />
    </>
  );
};

MyAddMenu.propTypes = {
  isLoggedUserClientPlus: PropTypes.bool,
  isAvailabilityRecordsEnabled: PropTypes.bool,
};

MyAddMenu.defaultProps = {
  isLoggedUserClientPlus: false,
  isAvailabilityRecordsEnabled: false,
};
